import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Paper,
  Typography,
  IconButton,
  Rating,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Seo from "../components/Seo";
import TrustPanel from "../components/TrustPanel";
import { SITE } from "../data/site";
import { dashboardReviews } from "../data/reviews";
import { buildBreadcrumbSchema, buildServiceSchema, buildWebPageSchema } from "../utils/schema";
import "./Services.css";

// Replace these with your actual dashboard/reporting example files when you have them
// const image1 = `${process.env.PUBLIC_URL}/assets/dashboardExample1.png`;
// const image2 = `${process.env.PUBLIC_URL}/assets/dashboardExample2.png`;
// const image3 = `${process.env.PUBLIC_URL}/assets/dashboardExample3.png`;
const heroBackgroundImage = `${process.env.PUBLIC_URL}/assets/Logo.svg`;

function ImageCarousel({ images }) {
  const [idx, setIdx] = React.useState(0);

  React.useEffect(() => {
    if (!Array.isArray(images)) return;

    images.forEach((img) => {
      const preloader = new Image();
      preloader.src = img.image;
    });
  }, [images]);

  if (!images || images.length === 0) return null;

  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);
  const next = () => setIdx((i) => (i + 1) % images.length);

  return (
    <Paper className="bws-gsl-carousel" elevation={0}>
      <div className="bws-gsl-carouselInner">
        <IconButton className="bws-gsl-carouselBtn" onClick={prev} aria-label="Previous example">
          <ChevronLeftIcon />
        </IconButton>

        <div className="bws-gsl-carouselFrame">
          {images.map((img, i) => (
            <img
              key={i}
              className={`bws-gsl-carouselImg ${i === idx ? "is-active" : "is-hidden"}`}
              src={img.image}
              alt={img.altText}
              loading="eager"
              decoding="async"
            />
          ))}
        </div>

        <IconButton className="bws-gsl-carouselBtn" onClick={next} aria-label="Next example">
          <ChevronRightIcon />
        </IconButton>
      </div>

      <div className="bws-gsl-carouselDots">
        {images.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`bws-gsl-dot ${i === idx ? "is-active" : ""}`}
            aria-label={`View example ${i + 1}`}
            onClick={() => setIdx(i)}
          />
        ))}
      </div>
    </Paper>
  );
}

export default function DashboardsReporting() {
  const safeReviews = Array.isArray(dashboardReviews) ? dashboardReviews : [];
  const title = "Dashboards & Reporting — Power BI, Tableau, Looker, KPI Dashboards";
  const description =
    "Turn raw data into clear dashboards and reporting that answer real business questions. KPI dashboards, monthly reporting, and cleaner business intelligence workflows.";
  const canonical = "https://brainwavesolutions.net/services/dashboards";
  const structuredData = [
    buildWebPageSchema({ title, description, canonical }),
    buildBreadcrumbSchema([
      { name: "Home", url: SITE.url },
      { name: "Services", url: `${SITE.url}/services` },
      { name: "Dashboards & Reporting", url: canonical },
    ]),
    buildServiceSchema({
      name: "Dashboards & Reporting",
      description,
      canonical,
      offerLabel: "$300–$2,500+",
      serviceType: "Dashboards and reporting",
    }),
  ];

  // Add/remove entries here as you get examples.
  // If this ends up empty, the whole Examples section disappears.
  const images = [
    // {
    //   image: image1,
    //   altText:
    //     "Business intelligence dashboard showing KPI summary cards, charts, and reporting widgets for operational decision making.",
    // },
    // {
    //   image: image2,
    //   altText:
    //     "Reporting dashboard layout with charts, filters, and trend views for business performance analysis.",
    // },
    // {
    //   image: image3,
    //   altText:
    //     "Executive reporting dashboard with summarized metrics, visual charts, and organized business insights.",
    // },
  ].filter((img) => img.image); // safe if you comment some out

  return (
    <>
      <Seo
        title={title}
        description={description}
        canonical={canonical}
        structuredData={structuredData}
      />

      <Box className="bws-gsl-page">
        <Container maxWidth="lg">
          <div className="bws-gsl-hero">
            <div
              className="bws-gsl-heroMain"
              style={{ "--gsl-bg-image": `url(${heroBackgroundImage})` }}
            >
              <Typography variant="h1" className="bws-gsl-title">
                Dashboards and reporting that make the numbers easier to understand
              </Typography>

              <p className="bws-gsl-lead">
                A lot of businesses already have the data — the problem is that it is scattered, inconsistent, or buried
                in exports that nobody wants to sort through. By the time a report is finally put together, it often
                raises more questions than it answers.
              </p>

              <p className="bws-gsl-lead bws-gsl-leadTight">
                I help turn raw data into dashboards and reporting that are clear, useful, and built around the way you
                actually run the business. The goal is not just prettier charts — it is faster visibility, better
                decisions, and less time spent assembling the same report every month.
              </p>

              <div className="bws-gsl-badges">
                <span className="bws-gsl-badge">KPI dashboards</span>
                <span className="bws-gsl-badge">Business reporting</span>
                <span className="bws-gsl-badge">Power BI / Tableau / Looker</span>
                <span className="bws-gsl-badge">Clearer decision-making</span>
              </div>

              <div className="bws-gsl-ctaRow">
                <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
                  Get help with reporting
                </Button>
              </div>

              <div className="bws-gsl-miniTrust">
                <span>Businesses &amp; teams</span>
                <span>•</span>
                <span>Clear communication</span>
                <span>•</span>
                <span>Practical solutions</span>
              </div>
            </div>

            <div className="bws-gsl-sideStack">
              <TrustPanel
                title="Work directly with Nate"
                body="You’re not sending your project into a big agency pipeline, you will work directly with me."
                badges={["Based in the USA", "Native English speaker"]}
              />

              <Paper className="bws-gsl-sideCard" elevation={0}>
                <Typography className="bws-gsl-sideTitle">Common reporting problems I solve</Typography>
                <ul className="bws-gsl-sideList">
                  <li>Reports that take too long to assemble manually</li>
                  <li>KPIs that are unclear or defined inconsistently</li>
                  <li>Exports and spreadsheets that don’t roll up cleanly</li>
                  <li>Dashboards that look nice but don’t answer real questions</li>
                  <li>Leadership needing faster visibility into performance</li>
                </ul>

                <Typography className="bws-gsl-sideNote">
                  Best first step: send a message with the data you have, the questions you need answered, and what kind of reporting would help most.
                </Typography>
              </Paper>
            </div>
          </div>

          <div className="bws-gsl-strip">
            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">See what matters faster</Typography>
              <Typography className="bws-gsl-stripText">
                Clear KPI views, trend reporting, and summaries that help you spot what needs attention.
              </Typography>
            </Paper>

            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Reduce manual reporting</Typography>
              <Typography className="bws-gsl-stripText">
                Spend less time assembling updates and more time using the information.
              </Typography>
            </Paper>

            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Give your team clarity</Typography>
              <Typography className="bws-gsl-stripText">
                Dashboards and reports that are easier to read, explain, and act on.
              </Typography>
            </Paper>
          </div>

          {images.length > 0 && (
            <div className="bws-gsl-section">
              <div className="bws-gsl-sectionHead">
                <Typography className="bws-gsl-h2">Examples:</Typography>
                <p className="bws-gsl-p">
                  Dashboard and reporting examples designed to make business performance easier to understand.
                </p>
              </div>

              <ImageCarousel images={images} />
            </div>
          )}

          <div className="bws-gsl-section">
            <div className="bws-gsl-grid">
              <Card className="bws-gsl-card" elevation={0}>
                <CardContent className="bws-gsl-cardContent">
                  <Typography className="bws-gsl-h2">What I help with</Typography>
                  <ul className="bws-gsl-list">
                    <li>KPI dashboard design and cleanup</li>
                    <li>Business intelligence reporting workflows</li>
                    <li>Power BI, Tableau, and Looker reporting support</li>
                    <li>Monthly reporting packages and executive summaries</li>
                    <li>Data preparation for clearer dashboards and metrics</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bws-gsl-card" elevation={0}>
                <CardContent className="bws-gsl-cardContent">
                  <Typography className="bws-gsl-h2">Good fit for</Typography>
                  <ul className="bws-gsl-list">
                    <li>Business owners who need clearer visibility</li>
                    <li>Operations teams tired of manual reporting</li>
                    <li>Leadership teams needing KPI summaries that make sense</li>
                    <li>Anyone sitting on data but lacking useful reporting</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {safeReviews.length > 0 && (
            <div className="bws-gsl-section">
              <div className="bws-gsl-sectionHead">
                <Typography className="bws-gsl-h2">Client feedback</Typography>
              </div>

              <div className="bws-gsl-reviews">
                {safeReviews.slice(0, 3).map((r, idx) => (
                  <Paper key={idx} className="bws-gsl-review" elevation={0}>
                    <Rating
                      value={5}
                      readOnly
                      size="small"
                      className="bws-gsl-reviewStars"
                      aria-label="5 out of 5 stars"
                    />
                    <Typography className="bws-gsl-reviewQuote">“{r.quote}”</Typography>
                    <Typography className="bws-gsl-reviewAuthor">— {r.author}</Typography>
                  </Paper>
                ))}
              </div>
            </div>
          )}

          <div className="bws-gsl-section">
            <Paper className="bws-gsl-faq" elevation={0}>
              <Typography className="bws-gsl-h2">Frequently asked questions</Typography>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">Can you help if I already have exports or existing reports?</Typography>
                <Typography className="bws-gsl-faqA">
                  Yes. In many cases, that is the best place to start. I can help clean up what you already have and turn it into something easier to use.
                </Typography>
              </div>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">Can you help define what KPIs we should be looking at?</Typography>
                <Typography className="bws-gsl-faqA">
                  Yes. Sometimes the biggest issue is not the charts — it is making sure the reporting is focused on the numbers that actually matter.
                </Typography>
              </div>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">What do you need from me to get started?</Typography>
                <Typography className="bws-gsl-faqA">
                  A short description of what you are trying to understand, what data you have, and what kind of reporting would be useful.
                </Typography>
              </div>
            </Paper>
          </div>

          <Paper className="bws-gsl-bottomCta" elevation={0}>
            <div>
              <Typography className="bws-ctaTitle">Need better reporting or dashboards?</Typography>
              <p className="bws-muted">
                Send a quick description of your reporting needs and what you want to see more clearly.
              </p>
            </div>
            <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
              Contact
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
}
